import { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Play,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { corelCommunication } from '../../lib/corel/communication';
import { sendToTerminal } from '../../lib/terminalLogger';
import { useApiCache } from '../../lib/useApiCache';

// Tipos baseados no novo payload
interface SmbCredential {
  id: number;
  name: string;
  username: string;
  password_enc: string;
  domain: string;
  active: boolean;
}

interface DirectoryConfig {
  paths: string[];
  credential_id: number;
}

interface ApiResponse {
  credentials: SmbCredential[];
  diretorios: Record<string, DirectoryConfig>;
}

type TestStatus = 'idle' | 'testing' | 'success' | 'error';

interface PathResult {
  path: string;
  status: TestStatus;
  method?: 'smb' | 'windows';
  error?: string;
}

interface CategoryData {
  key: string;
  label: string;
  paths: string[];
  credential: SmbCredential | null;
  status: TestStatus;
  expanded: boolean;
  results: PathResult[];
}

const LABELS: Record<string, string> = {
  interno: 'Interno',
  externo: 'Externo',
  exportpng: 'Export PNG',
  export_prod: 'Produção',
  pre: 'Pré',
  gerenciar: 'Gerenciar'
};

const ORDER = ['interno', 'externo', 'exportpng', 'export_prod', 'pre', 'gerenciar'];

export function TestSmbPage() {
  const [refreshing, setRefreshing] = useState(false);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentIdx, setCurrentIdx] = useState<number | null>(null);

  // ✅ PADRONIZADO: Usar useApiCache hook centralizado
  const { data, loading, error, refresh } = useApiCache<ApiResponse>(
    'test-smb-config',
    '/cdr/config/diretorios',
    {
      ttlMs: 5 * 60 * 1000, // 5 minutos
      validate: (data) => {
        // Validar estrutura básica
        if (!data || typeof data !== 'object') return false;
        if (!Array.isArray(data.credentials)) return false;
        if (!data.diretorios || typeof data.diretorios !== 'object') return false;
        return true;
      }
    }
  );

  // Processar dados quando carregarem
  useEffect(() => {
    if (!data) return;

    const creds = data?.credentials || [];
    const dirs = data?.diretorios || {};
    
    // Montar categorias com credencial específica
    const cats: CategoryData[] = [];
    ORDER.forEach(key => {
      const dirConfig = dirs[key];
      if (dirConfig && Array.isArray(dirConfig.paths) && dirConfig.paths.length > 0) {
        // Encontrar credencial pelo ID
        const credential = creds.find(c => c.id === dirConfig.credential_id) || null;
        
        cats.push({
          key,
          label: LABELS[key] || key,
          paths: dirConfig.paths,
          credential,
          status: 'idle',
          expanded: false,
          results: dirConfig.paths.map(p => ({ path: p, status: 'idle' as TestStatus }))
        });
      }
    });
    
    setCategories(cats);
    sendToTerminal('✅ [TESTSMB] Config carregada', { 
      categorias: cats.length, 
      creds: creds.length 
    });
  }, [data]);

  // Função de refresh manual (mantida para compatibilidade)
  const fetchConfig = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
      try {
        await refresh();
        sendToTerminal('🔄 [TESTSMB] Configuração atualizada');
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        sendToTerminal('❌ [TESTSMB] Erro ao atualizar config', { error: errorMsg });
      } finally {
        setRefreshing(false);
      }
    }
  }, [refresh]);

  // Testar um caminho com a credencial específica
  const testPath = useCallback(async (path: string, credential: SmbCredential | null): Promise<PathResult> => {
    return new Promise(resolve => {
      const payload: any = { diretorio: path };
      
      if (credential) {
        payload.smb = { 
          username: credential.username, 
          password: credential.password_enc, 
          domain: credential.domain || '' 
        };
      }

      sendToTerminal('🔌 [TESTSMB] Testando', { 
        path: path.slice(-30), 
        cred: credential?.name || 'Windows' 
      });

      const timeout = setTimeout(() => {
        sendToTerminal('⏱️ [TESTSMB] Timeout', { path: path.slice(-30) });
        resolve({ path, status: 'error', error: 'Timeout (15s)' });
      }, 15000);

      corelCommunication.sendActionGuarded('testar-conexao-smb', payload, (res: any) => {
        clearTimeout(timeout);
        const result = res?.result || {};
        
        if (res?.status === 'ok' && result.connectionSuccess) {
          sendToTerminal('✅ [TESTSMB] Sucesso', { 
            path: path.slice(-30), 
            method: result.windowsFallbackUsed ? 'windows' : 'smb' 
          });
          resolve({ 
            path, 
            status: 'success', 
            method: result.windowsFallbackUsed ? 'windows' : 'smb' 
          });
        } else {
          const errorMsg = res?.message || result?.errorMessage || 'Falha na conexão';
          sendToTerminal('❌ [TESTSMB] Falha', { path: path.slice(-30), error: errorMsg });
          resolve({ 
            path, 
            status: 'error', 
            error: errorMsg
          });
        }
      });
    });
  }, []);

  // Testar categoria (usa a credencial específica da categoria)
  const testCategory = useCallback(async (idx: number) => {
    const cat = categories[idx];
    if (!cat) return;

    setCategories(prev => prev.map((c, i) => 
      i === idx ? { 
        ...c, 
        status: 'testing', 
        expanded: true, 
        results: c.paths.map(p => ({ path: p, status: 'idle' as TestStatus })) 
      } : c
    ));
    setCurrentIdx(idx);

    const results: PathResult[] = [];

    for (let i = 0; i < cat.paths.length; i++) {
      // Marcar testando
      setCategories(prev => prev.map((c, ci) => 
        ci === idx ? { 
          ...c, 
          results: c.results.map((r, ri) => ri === i ? { ...r, status: 'testing' as TestStatus } : r) 
        } : c
      ));

      // Testar com a credencial específica da categoria
      const result = await testPath(cat.paths[i], cat.credential);
      results.push(result);

      // Atualizar resultado
      setCategories(prev => prev.map((c, ci) => 
        ci === idx ? { 
          ...c, 
          results: c.results.map((r, ri) => ri === i ? result : r) 
        } : c
      ));

      await new Promise(r => setTimeout(r, 300));
    }

    // Status final
    const allOk = results.every(r => r.status === 'success');
    setCategories(prev => prev.map((c, i) => 
      i === idx ? { ...c, status: allOk ? 'success' : 'error', results } : c
    ));
  }, [categories, testPath]);

  const runAll = useCallback(async () => {
    if (isRunning) return;
    setIsRunning(true);
    sendToTerminal('🚀 [TESTSMB] Iniciando testes de todas as categorias');
    
    // Reset
    setCategories(prev => prev.map(c => ({ 
      ...c, 
      status: 'idle', 
      expanded: false, 
      results: c.paths.map(p => ({ path: p, status: 'idle' as TestStatus }))
    })));

    for (let i = 0; i < categories.length; i++) {
      await testCategory(i);
    }

    setCurrentIdx(null);
    setIsRunning(false);
    sendToTerminal('✅ [TESTSMB] Testes concluídos');
  }, [categories, testCategory, isRunning]);

  const runOne = useCallback(async (idx: number) => {
    if (isRunning) return;
    setIsRunning(true);
    await testCategory(idx);
    setCurrentIdx(null);
    setIsRunning(false);
  }, [testCategory, isRunning]);

  const toggle = (idx: number) => {
    setCategories(prev => prev.map((c, i) => i === idx ? { ...c, expanded: !c.expanded } : c));
  };

  // Stats
  const totalOk = categories.reduce((s, c) => s + c.results.filter(r => r.status === 'success').length, 0);
  const totalErr = categories.reduce((s, c) => s + c.results.filter(r => r.status === 'error').length, 0);
  const totalPaths = categories.reduce((s, c) => s + c.paths.length, 0);

  const StatusIcon = ({ status }: { status: TestStatus }) => {
    switch (status) {
      case 'testing': return <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />;
      case 'success': return <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />;
      case 'error': return <XCircle className="w-3.5 h-3.5 text-red-400" />;
      default: return <Clock className="w-3.5 h-3.5 text-gray-600" />;
    }
  };

  // Loading inicial
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto mb-2" />
          <p className="text-xs text-gray-500">Carregando configuração...</p>
        </div>
      </div>
    );
  }

  // Erro
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 p-4">
        <XCircle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-red-300 text-center">{error}</p>
        <button 
          onClick={() => fetchConfig()} 
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
        >
          <RefreshCw className="w-3 h-3" /> Tentar novamente
        </button>
      </div>
    );
  }

  // Sem categorias
  if (categories.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 p-4">
        <p className="text-sm text-gray-400">Nenhum diretório configurado</p>
        <button 
          onClick={() => fetchConfig()} 
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
        >
          <RefreshCw className="w-3 h-3" /> Recarregar
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 p-3 overflow-y-auto space-y-3">
        
        {/* Header compacto com refresh */}
        <div className="flex items-center justify-between text-[11px] px-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">{totalPaths} caminhos</span>
            <button
              onClick={() => fetchConfig(true)}
              disabled={refreshing || isRunning}
              className={`p-1 rounded transition-colors ${
                refreshing || isRunning 
                  ? 'text-gray-700 cursor-not-allowed' 
                  : 'text-gray-500 hover:text-blue-400 hover:bg-navy-700/50'
              }`}
              title="Recarregar configuração"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {(totalOk > 0 || totalErr > 0) && (
            <span>
              <span className="text-green-400">{totalOk} ok</span>
              {totalErr > 0 && <span className="text-red-400 ml-2">{totalErr} erro</span>}
            </span>
          )}
        </div>

        {/* Categorias */}
        <div className="space-y-2">
          {categories.map((cat, idx) => (
            <div
              key={cat.key}
              className={`rounded-lg border overflow-hidden transition-colors ${
                cat.status === 'testing' ? 'border-blue-500/40 bg-blue-500/5' :
                cat.status === 'success' ? 'border-green-500/30 bg-green-500/5' :
                cat.status === 'error' ? 'border-red-500/30 bg-red-500/5' :
                'border-navy-600/50 bg-navy-800/40'
              }`}
            >
              {/* Header */}
              <div className="flex items-center p-2.5 gap-2">
                <button onClick={() => toggle(idx)} className="flex items-center gap-2 flex-1 min-w-0">
                  {cat.expanded 
                    ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" /> 
                    : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  }
                  <StatusIcon status={cat.status} />
                  <span className="text-sm text-gray-200 font-medium truncate">{cat.label}</span>
                  
                  {/* Credencial da categoria */}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                    cat.credential ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {cat.credential?.name || 'Windows'}
                  </span>
                  
                  {/* Status count */}
                  {cat.status !== 'idle' && cat.status !== 'testing' && (
                    <span className={`text-[10px] flex-shrink-0 ${
                      cat.results.some(r => r.status === 'error') ? 'text-red-400' : 'text-green-400'
                    }`}>
                      {cat.results.filter(r => r.status === 'success').length}/{cat.paths.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => runOne(idx)}
                  disabled={isRunning}
                  className={`p-1.5 rounded flex-shrink-0 transition-colors ${
                    isRunning ? 'text-gray-700 cursor-not-allowed' : 'text-gray-500 hover:text-blue-400 hover:bg-navy-700/50'
                  }`}
                  title="Testar esta categoria"
                >
                  {currentIdx === idx 
                    ? <Loader2 className="w-4 h-4 animate-spin text-blue-400" /> 
                    : <Play className="w-4 h-4" />
                  }
                </button>
              </div>

              {/* Detalhes expandidos */}
              {cat.expanded && (
                <div className="border-t border-navy-700/40 bg-navy-900/40 divide-y divide-navy-700/30">
                  {cat.results.map((r, ri) => (
                    <div key={ri} className="px-3 py-2 flex items-center gap-2 text-[11px]">
                      <StatusIcon status={r.status} />
                      <span className="text-gray-400 flex-1 truncate" title={r.path}>
                        ...{r.path.slice(-35)}
                      </span>
                      {r.status === 'success' && (
                        <span className="text-green-400 flex-shrink-0">
                          {r.method === 'windows' ? 'Windows' : 'OK'}
                        </span>
                      )}
                      {r.status === 'error' && (
                        <span className="text-red-400 flex-shrink-0 truncate max-w-[120px]" title={r.error}>
                          {r.error}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-navy-700">
        <button 
          onClick={runAll}
          disabled={isRunning || refreshing}
          className={`w-full py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
            isRunning || refreshing 
              ? 'bg-blue-700/70 text-white/70 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white'
          }`}
        >
          {isRunning 
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Testando...</> 
            : <><Play className="w-4 h-4" /> Testar Todas</>
          }
        </button>
      </div>
    </div>
  );
}
